import type { components } from '../api/generated/schema'

/**
 * The generated DTOs have *every* member optional, because Swashbuckle does not
 * emit `required` for non-nullable C# reference types. Rather than spread that
 * uncertainty through the app, the wire shapes are checked once, here, and the
 * rest of the client works with types that mean what they say.
 */
type TokenResponseDto = components['schemas']['TokenResponse']
type MeResponseDto = components['schemas']['MeResponse']

export interface AuthTokens {
  accessToken: string
  refreshToken: string
  /** Seconds until the access token expires. 15 minutes, per the API's Jwt settings. */
  expiresIn: number
}

export interface CurrentUser {
  id: string
  email: string | null
  displayName: string | null
  emailConfirmed: boolean
  roles: string[]
}

class MalformedResponseError extends Error {
  constructor(what: string) {
    super(`The API returned a ${what} missing required fields.`)
    this.name = 'MalformedResponseError'
  }
}

export function toAuthTokens(dto: TokenResponseDto): AuthTokens {
  if (
    dto.accessToken === undefined ||
    dto.refreshToken === undefined ||
    dto.expiresIn === undefined
  ) {
    throw new MalformedResponseError('token response')
  }

  return {
    accessToken: dto.accessToken,
    refreshToken: dto.refreshToken,
    expiresIn: dto.expiresIn,
  }
}

export function toCurrentUser(dto: MeResponseDto): CurrentUser {
  if (dto.id === undefined) {
    throw new MalformedResponseError('user profile')
  }

  return {
    id: dto.id,
    email: dto.email ?? null,
    displayName: dto.displayName ?? null,
    emailConfirmed: dto.emailConfirmed ?? false,
    roles: dto.roles ?? [],
  }
}

/** Roles are global (Admin); Game Master is per-campaign and comes from campaign responses. */
export function isAdmin(user: CurrentUser | null): boolean {
  return user?.roles.includes('Admin') ?? false
}

/** What to show as the account's name. Display name is optional in Identity. */
export function displayNameOf(user: CurrentUser): string {
  return user.displayName ?? user.email ?? 'Signed in'
}
